import TeamListPagination from '@features/organisation/components/teamList/TeamListPagination'
import type { DeviceTrailListResponse } from '@lmc/ts-common'
import ResidentCell from '@lmc/ui-kit/src/legacy/molecules/Table/cells/ResidentCell'
import Table from '@lmc/ui-kit/src/legacy/molecules/Table/Table'
import type { TablePaginationConfig } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import useUsersList from 'hooks/useUser/useUsersList'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { DEVICES_PER_PAGE, type TableParams } from '../../types'
import { getLocationItemType } from '../../utils/devices'
import { cssDeviceTable } from './deviceTableStyles'

type DevicesSignsTableProps = {
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters?: Record<string, FilterValue | null>,
    sorter?: SorterResult<unknown>
  ) => void
  isLoading?: boolean
  signs?: DeviceTrailListResponse[]
  tableParams: TableParams
  testId?: string
}

type DataSource = {
  carer: string
  deviceName: string
  key: string
  location: {
    type: DeviceTrailListResponse['type']
    signIn: DeviceTrailListResponse['signIn']
  }
  timestamp: string
}

const DevicesSignsTable: FC<DevicesSignsTableProps> = ({
  signs,
  isLoading,
  tableParams,
  handleTableChange,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const rows: DataSource[] | undefined = signs?.map((sign) => ({
    deviceName: sign?.name,
    carer: sign?.user.id,
    location: {
      type: sign?.type,
      signIn: sign?.signIn
    },
    timestamp: dayjs(sign.timestamp).format('HH:mm DD/MM/YYYY '),
    key: sign?.id
  }))

  const { users } = useUsersList({})

  const pageSize = tableParams?.pagination?.pageSize ?? DEVICES_PER_PAGE
  const totalItems = tableParams?.pagination?.total ?? 0
  const currentPage = tableParams?.pagination?.current ?? 1
  const totalPages = Math.ceil(totalItems / pageSize)

  // Sorting still flows through antd's onChange, but its pagination arg is empty
  // while the built-in pager is disabled — forward the current pagination so it
  // isn't wiped when the user sorts.
  const handleSort = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<DataSource> | SorterResult<DataSource>[]
  ) =>
    handleTableChange(
      tableParams.pagination ?? {},
      filters,
      sorter as SorterResult<unknown>
    )

  const handlePageChange = (page: number) =>
    handleTableChange({ ...tableParams.pagination, current: page })

  const columns: ColumnsType<DataSource> = [
    {
      dataIndex: 'timestamp',
      width: '26%',
      title: t(dacConstants.SIGN_IN_TIME),
      sortDirections: ['descend', 'ascend'],
      sorter: true
    },
    {
      dataIndex: 'deviceName',
      width: '22%',
      title: t(dacConstants.DEVICE_NAME),
      sortDirections: ['descend', 'ascend'],
      sorter: true
    },
    {
      dataIndex: 'carer',
      width: '28%',
      title: t(dacConstants.TEAM_MEMBER),
      render: (id: string) => {
        const user = users.find((u) => u.id === id)
        return (
          user && (
            <ResidentCell
              avatarSize={30}
              resident={{
                name: user.name,
                picture: user.picture as unknown as string
              }}
            />
          )
        )
      },
      sortDirections: ['descend', 'ascend'],
      sorter: true
    },
    {
      dataIndex: 'location',
      title: t(dacConstants.LOCATION),
      render: ({ type, signIn }) => getLocationItemType(type, t, signIn)
    }
  ]

  return (
    <>
      <TableStyled
        columns={columns}
        data-testid={testId}
        dataSource={rows}
        loading={isLoading}
        onChange={handleSort}
        pagination={false}
        tableLayout="fixed"
      />
      <TeamListPagination
        currentPage={currentPage}
        onPageChange={handlePageChange}
        testId={testId ? `${testId}-pagination` : undefined}
        totalPages={totalPages}
      />
    </>
  )
}

const TableStyled = styled(Table)`
  ${cssDeviceTable}
` as typeof Table

export default DevicesSignsTable
